import { SagaIterator } from 'redux-saga';
import { call, select, takeEvery } from 'redux-saga/effects';
import {
  AddVertragToAngebote,
  GlobalActions,
  GlobalPut,
  ModalType,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  areAppTypesEquivalent,
  convertToVorvertragskennzeichen,
  FEVorvertragsnummer,
  normalizeApp,
  StandaloneProdukt,
  TkvV2Vorbelegungdaten,
  Vorvertrag,
} from '@ps20-frontend/frontend-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { PS20Logger } from '@ps20-frontend/logging';
import { IAppState } from '@ps20-frontend/apps/dashboard-store';
import { DashboardAction, DashboardActions } from '../dashboard.actions';
import {
  isStandaloneVertrag,
  isVorvertrag,
  selectAlleVorvertraegeForModal,
  selectInitializedVorvertraege,
  selectSelectedWeiterenVertrag,
  selectSomeCheckboxCanBeChecked,
} from '../selectors/vorvertragsUebersicht.selector';
import { StandaloneDialogModel } from '../model/standaloneDialog';
import {
  VorvertragDataForModal,
  VorvertragForModal,
} from '../../app/vertraege-ersetzen-auswahl-modal/vertraege-ersetzen-auswahl-modal.component';
import { getHinweisTextForModal } from './multiVertragSaga';

export function* vorvertragUmstellenWatch(): SagaIterator {
  yield takeEvery(
    DashboardActions.VORVERTRAG_UMSTELLEN,
    safeSaga(vorvertragUmstellen, App.SHOP_DASHBOARD),
  );
}

export function* vorvertragUmstellen(action: DashboardAction<Vorvertrag>): SagaIterator {
  const vertrag = action.payload;
  const state = yield select();
  const alleVorvertraegeForModal: Array<Vorvertrag | StandaloneProdukt> =
    removeVWGVorvertrageWithoutOptions(
      selectAlleVorvertraegeForModal(state, vertrag, true),
      vertrag,
    );
  const isWeitererVertragWithRsOrVwg =
    (selectSelectedWeiterenVertrag(state, vertrag) && areAppTypesEquivalent(vertrag.produktIds[0], App.VWG)) ||
    vertrag.produktIds[0] === App.RS;
  if (alleVorvertraegeForModal.length > 0 || isWeitererVertragWithRsOrVwg) {
    const initializedVorvertraege: VorvertragDataForModal = selectInitializedVorvertraege(
      alleVorvertraegeForModal as Vorvertrag[],
      vertrag,
    );
    if (!anyCheckableVertrag(initializedVorvertraege, state)) {
      yield call(addVertragToAngebote, vertrag, alleVorvertraegeForModal, true);
      const vorvertragsNummernZurUmstellung: FEVorvertragsnummer[] = [];
      initializedVorvertraege.vorvertragForModalArray.forEach(vertrag => {
        vorvertragsNummernZurUmstellung.push({
          vertragsnummer: vertrag.vorvertrag.vertragsnummer,
          spLaufnummer: vertrag.vorvertrag.spLaufnummer,
          linkToSAbs: vertrag.vorvertrag.linkToSAbs,
          firmenvertrag: vertrag.vorvertrag.firmenvertrag,
        } as FEVorvertragsnummer);
      });
      if (vorvertragsNummernZurUmstellung.length >= 1) {
        yield call(
          GlobalPut.getInstance().put,
          new GlobalActions().requestModal(ModalType.MULTIPLE_RS_VORVERTRAEGE, {
            body: getHinweisTextForModal(vorvertragsNummernZurUmstellung),
          }),
        );
      }
    } else {
      const standalonePayload = {
        appId: vertrag.produktIds[0],
        vorvertrag: vertrag,
      } as StandaloneDialogModel;
      yield call(
        GlobalPut.getInstance().put,
        new DashboardActions().requestStandaloneProduktDialog(standalonePayload),
      );
    }
  } else {
    yield call(addVertragToAngebote, vertrag);
  }
}

function anyCheckableVertrag(
  initializedVorvertraege: VorvertragDataForModal,
  state: IAppState,
): boolean {
  for (const vertrag of initializedVorvertraege.vorvertragForModalArray) {
    if (selectSelectedWeiterenVertrag(state, vertrag.vorvertrag)) {
      return true;
    }
    if (isStandaloneVertrag(vertrag.vorvertrag)) {
      return true;
    }
    if (vertrag.vorvertrag.produktIds[0] === App.RS && vertrag.vorvertrag.firmenvertrag !== false) {
      return true;
    }
    if (
      vertrag.vorvertrag.produktIds[0] !== App.RS &&
      vertrag.vorvertrag.produktIds[0] !== App.RS_VERMIETUNG &&
      !vertrag.disabled
    ) {
      return true;
    }
  }
  return false;
}

export function* addVertragToAngebote(
  vertrag: Vorvertrag,
  vertraege: Array<Vorvertrag | StandaloneProdukt> = [],
  noUmgestellteIdsHinweis = false,
): SagaIterator {
  const vorvertragsNummern = [
    {
      vertragsnummer: vertrag.vertragsnummer,
      spLaufnummer: vertrag.spLaufnummer,
      linkToSAbs: vertrag.linkToSAbs,
      vorbelegungForBeibehaltung: vertrag.vorbelegungdaten?.[0],
    },
  ];
  vertraege?.forEach(additionalVertrag => {
    vorvertragsNummern.push({
      vertragsnummer: additionalVertrag.vertragsnummer,
      spLaufnummer: additionalVertrag.spLaufnummer,
      linkToSAbs: additionalVertrag.linkToSAbs,
      vorbelegungForBeibehaltung: additionalVertrag.vorbelegungdaten?.[0],
    });
  });
  const state = (yield select()) as IAppState;
  let produktId = vertrag.produktIds[0];
  const sourceAppType = vertrag.produktIds[0]; // Original app type from loaded contract
  const beibehalten = false; // This is "umstellen" operation

  produktId = normalizeApp(produktId, {
    sourceAppType: sourceAppType,
    beibehalten: beibehalten,
    featureToggles: state.transientState?.featureToggles as { [key: string]: boolean },
  });
  const payload: AddVertragToAngebote = {
    appId: produktId,
    vorvertragsNummern: vorvertragsNummern,
    vorvertragForVorbelegung: {
      vertragsnummer: vertrag.vertragsnummer,
      spLaufnummer: vertrag.spLaufnummer,
    },
    beibehalten: beibehalten,
    vorvertragSofortschutz: vertrag.vorvertragSofortschutz,
    vertragskennzeichen: convertToVorvertragskennzeichen(vertrag?.vertragskennzeichen),
    noUmgestellteIdsHinweis,
  };

  if (vertrag.produktlinieAnzeigename) {
    payload.linieOfVorvertrag = vertrag.produktlinieAnzeigename;
  }
  if (sourceAppType === App.TKV_V2) {
    PS20Logger.info(
      'TKV_V2.Umstellen.payloadBuilt',
      JSON.stringify({
        produktId,
        sourceAppType,
        vorbelegungTierart: (vertrag.vorbelegungdaten?.[0] as TkvV2Vorbelegungdaten | undefined)?.tierart,
        payloadHasTierart: 'tierart' in payload,
        vorvertragsNummer: vertrag.vertragsnummer,
      }),
      App.SHOP_DASHBOARD,
    );
  }
  yield call(GlobalPut.getInstance().put, new GlobalActions().requestAddVertragToAngebote(payload));
}

function removeVWGVorvertrageWithoutOptions(
  alleVorvertraegeForModal: Array<Vorvertrag | StandaloneProdukt>,
  selectedVorvertrag: Vorvertrag,
): Array<Vorvertrag | StandaloneProdukt> {
  let einschliessbareVorvertraege: Vorvertrag[] = alleVorvertraegeForModal
    .filter(vv => isVorvertrag(vv))
    .map(vv => vv as Vorvertrag);

  const einschliessbareVorvertraegeVWG: Vorvertrag[] = einschliessbareVorvertraege.filter(vv =>
    vv.produktIds.some(id => areAppTypesEquivalent(id, App.VWG)),
  );

  if (!einschliessbareVorvertraegeVWG || einschliessbareVorvertraegeVWG.length < 1) {
    return alleVorvertraegeForModal;
  }

  const einschliessbareStandaloneProdukte: StandaloneProdukt[] = alleVorvertraegeForModal
    .filter(vv => isStandaloneVertrag(vv))
    .map(vv => vv as StandaloneProdukt);

  const initializedVorvertraege: VorvertragDataForModal = selectInitializedVorvertraege(
    einschliessbareVorvertraegeVWG,
    selectedVorvertrag,
  );

  if (
    initializedVorvertraege.vorvertragForModalArray &&
    initializedVorvertraege.vorvertragForModalArray.length > 0
  ) {
    einschliessbareVorvertraege = einschliessbareVorvertraege.filter(
      einschliessbarerVorvertraeg => {
        const matchingInitializedVorvertrag: VorvertragForModal =
          initializedVorvertraege.vorvertragForModalArray.find(
            vv =>
              vv.vorvertrag.vertragsnummer === einschliessbarerVorvertraeg.vertragsnummer &&
              vv.vorvertrag.spLaufnummer === einschliessbarerVorvertraeg.spLaufnummer,
          );

        return matchingInitializedVorvertrag
          ? selectSomeCheckboxCanBeChecked(matchingInitializedVorvertrag)
          : true;
      },
    );
  }
  return [...einschliessbareVorvertraege, ...einschliessbareStandaloneProdukte];
}
